import { Injectable, inject } from '@angular/core';
import {
  DismissFindingRequest,
  IncomeTestAdjustmentRequest,
  PartnerAssetUpdateRequest,
  ReadDocumentsRequest,
} from '@interfaces/partner-income-tax/requests/partner-income-tax.request';
import {
  PartnerIncomeTaxDocumentDto,
  PartnerIncomeTaxDocumentsReadResponse,
  PartnerIncomeTaxOverviewDto,
  PartnerIncomeTaxPartnerDto,
  PartnerIncomeTaxReprocessResponse,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { VaultApiService } from '@services/api/vault-api.service';
import { Observable } from 'rxjs';

const BASE = 'partner-income-tax';

/**
 * IR dos sócios (card 3515): `api/v1/partner-income-tax`. Leitura exige `PARTNERINCOMETAX_VIEW`; escrita,
 * `PARTNERINCOMETAX_UPDATE`. Toda escrita no sócio devolve o sócio inteiro recalculado pelo backend.
 */
@Injectable({ providedIn: 'root' })
export class PartnerIncomeTaxService {
  private readonly api = inject(VaultApiService);

  getOverview(companyId: string): Observable<PartnerIncomeTaxOverviewDto | null> {
    return this.api.get<PartnerIncomeTaxOverviewDto>(`${BASE}/companies/${companyId}/overview`);
  }

  getPartner(
    companyId: string,
    personId: string,
    calendarYear?: number | null
  ): Observable<PartnerIncomeTaxPartnerDto | null> {
    const params = calendarYear ? { calendarYear } : undefined;
    return this.api.get<PartnerIncomeTaxPartnerDto>(
      `${BASE}/companies/${companyId}/partners/${personId}`,
      undefined,
      params
    );
  }

  adjustIncomeTest(
    returnId: string,
    request: IncomeTestAdjustmentRequest
  ): Observable<PartnerIncomeTaxPartnerDto | null> {
    return this.api.put<PartnerIncomeTaxPartnerDto>(
      `${BASE}/returns/${returnId}/income-test`,
      request
    );
  }

  /** Volta os cinco campos do teste de renda aos valores da declaração. */
  restoreIncomeTest(returnId: string): Observable<PartnerIncomeTaxPartnerDto | null> {
    return this.api.delete<PartnerIncomeTaxPartnerDto>(`${BASE}/returns/${returnId}/income-test`);
  }

  updateAsset(
    assetId: string,
    request: PartnerAssetUpdateRequest
  ): Observable<PartnerIncomeTaxPartnerDto | null> {
    return this.api.put<PartnerIncomeTaxPartnerDto>(`${BASE}/assets/${assetId}`, request);
  }

  dismissFinding(
    personId: string,
    pointId: string,
    request: DismissFindingRequest
  ): Observable<PartnerIncomeTaxPartnerDto | null> {
    return this.api.post<PartnerIncomeTaxPartnerDto>(
      `${BASE}/partners/${personId}/findings/${encodeURIComponent(pointId)}/dismiss`,
      request
    );
  }

  /** O `VaultApiService.delete` manda o segundo argumento no corpo; o ano vai na query, montado aqui. */
  undoDismissFinding(
    personId: string,
    pointId: string,
    calendarYear: number
  ): Observable<PartnerIncomeTaxPartnerDto | null> {
    return this.api.delete<PartnerIncomeTaxPartnerDto>(
      `${BASE}/partners/${personId}/findings/${encodeURIComponent(pointId)}/dismiss?calendarYear=${calendarYear}`
    );
  }

  reprocess(companyId: string): Observable<PartnerIncomeTaxReprocessResponse | null> {
    return this.api.post<PartnerIncomeTaxReprocessResponse>(
      `${BASE}/companies/${companyId}/reprocess`,
      {}
    );
  }

  /** Documentos da empresa que a feature lê, com o status de leitura de cada um (VIEW). */
  getDocuments(companyId: string): Observable<PartnerIncomeTaxDocumentDto[] | null> {
    return this.api.get<PartnerIncomeTaxDocumentDto[]>(`${BASE}/companies/${companyId}/documents`);
  }

  /** Manda ler os documentos escolhidos (UPDATE). Os que não podem ser lidos voltam em `skipped`, com o motivo. */
  readDocuments(
    companyId: string,
    request: ReadDocumentsRequest
  ): Observable<PartnerIncomeTaxDocumentsReadResponse | null> {
    return this.api.post<PartnerIncomeTaxDocumentsReadResponse>(
      `${BASE}/companies/${companyId}/documents/read`,
      request
    );
  }
}
